// ─────────────────────────────────────────────────────────────────────────────
// Reporting period — the calendar window every tab aggregates over.
//
// Bode/SBB request (J. Funk, 18 Sep): be able to switch cycle figures between
// per day, per week, per month, and a free range from x to y.
//
// The brief said 'Zyklen pro Montag'. Read in the sequence day → week → ? →
// range, that is almost certainly a slip for 'pro Monat', so this is a month.
// Restoring a per-Monday option means one more entry in PERIOD_KEYS and one
// more branch below — the weekday profile it would need is already here.
//
// Kept in its own module so both data/doors.ts (fleet overview) and
// data/maintenance.ts (condition trending) can use it without importing each
// other.
// ─────────────────────────────────────────────────────────────────────────────

/** Length of the rolling window the demo data covers. */
export const WINDOW_DAYS = 90;

/** Calendar date for a day index. 0 = oldest in window, WINDOW_DAYS-1 = today. */
export function dateForIndex(i: number): Date {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() - (WINDOW_DAYS - 1 - i));
  return d;
}

export function formatDay(i: number, locale: string): string {
  return dateForIndex(i).toLocaleDateString(locale, { day: '2-digit', month: 'short' });
}

/** ISO yyyy-mm-dd, for <input type="date">. */
export function isoForIndex(i: number): string {
  const d = dateForIndex(i);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Nearest day index for an ISO date, clamped to the window. */
export function indexForIso(iso: string): number {
  const target = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(target.getTime())) return WINDOW_DAYS - 1;
  const today = dateForIndex(WINDOW_DAYS - 1);
  const diff = Math.round((today.getTime() - target.getTime()) / 86400000);
  return Math.max(0, Math.min(WINDOW_DAYS - 1, WINDOW_DAYS - 1 - diff));
}

/**
 * Traffic multiplier by weekday, Sunday … Saturday. Monday carries the
 * commuter peak; weekends fall away. Every period sums real days, so a week
 * and a month carry their own weekday mix rather than a flat average.
 */
export const DOW_FACTOR = [0.55, 1.09, 1.0, 0.99, 1.02, 1.07, 0.62];

export type PeriodKey = 'day' | 'week' | 'month' | 'range';

export const PERIOD_KEYS: PeriodKey[] = ['day', 'week', 'month', 'range'];

export interface PeriodSelection {
  key: PeriodKey;
  /** Only meaningful when key === 'range'. */
  fromIndex: number;
  toIndex: number;
}

export const defaultPeriod = (): PeriodSelection => ({
  key: 'day',
  fromIndex: WINDOW_DAYS - 28,
  toIndex: WINDOW_DAYS - 1,
});

/** Which day indices a selection covers. */
export function indicesFor(sel: PeriodSelection): number[] {
  const last = WINDOW_DAYS - 1;

  if (sel.key === 'day') return [last];

  if (sel.key === 'week') {
    return Array.from({ length: 7 }, (_, k) => last - 6 + k);
  }

  // Trailing 30 days, to match 'day' and 'week' being trailing windows too.
  if (sel.key === 'month') {
    return Array.from({ length: 30 }, (_, k) => last - 29 + k);
  }

  const lo = Math.max(0, Math.min(sel.fromIndex, sel.toIndex));
  const hi = Math.min(last, Math.max(sel.fromIndex, sel.toIndex));
  return Array.from({ length: hi - lo + 1 }, (_, k) => lo + k);
}
